import { FocusEvent, useCallback } from "react";
import { FormikErrors } from "formik";
import {
    getComponentByAttribute,
    getAttributePlaceHolderComponent
} from "@utils/attribute-input-util";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { AsyncAttributeInput } from "../../inputs/async-attribute-input/async-attribute-input";
import { ReadOnlyAttributeInput } from "../../inputs/read-only-attribute-input/read-only-attribute-input";
import { PickerAttributeInput } from "../../inputs/picker-attribute-input/picker-attribute-input";
import { PinInputContainer } from "../../inputs/pin-input/pin-input-container";
import styles from "./generic-input-component.less";

type GenericInputComponentProps = {
	attribute: AttributeFormState;
    attributeValuesPinIndex: number;
    attributeLabel: string;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
    pinFieldShouldBeAutofocused: boolean;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    handleAddNewSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdateSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdatePickerValue: (payload: { attribute: AttributeFormState; pickerData: any }) => void;
	isSubmitting: boolean;
};

export const GenericInputComponent = (props: GenericInputComponentProps) => {
    const {
		attribute,
        attributeValuesPinIndex,
        attributeLabel,
		fieldName,
		fieldTouched,
		fieldError,
		fieldIsInvalid,
		fieldShouldBeHighlighted,
        pinFieldShouldBeAutofocused,
		setFieldTouched,
		setFieldValue,
		onBlur,
        handleUpdateSubmitValue,
        handleAddNewSubmitValue,
		handleUpdatePickerValue,
		isSubmitting
	} = props;

	const InputComponent = getComponentByAttribute(attribute);
	const PlaceholderComponent = getAttributePlaceHolderComponent(attribute);

    const onSetSubmitValue = useCallback((payload: any) => {
		handleUpdateSubmitValue({ attribute, ...payload });
	}, [attribute, handleUpdateSubmitValue]);

	const onSetPickerValue = useCallback((payload: any) => {
		handleUpdatePickerValue({ attribute, ...payload });
	}, [attribute, handleUpdatePickerValue]);

	const onAddNewSubmitValue = useCallback((newInputData: any) => {
		handleAddNewSubmitValue({ attribute, inputData: newInputData });
	}, [attribute, handleAddNewSubmitValue]);

    return (
        <div className={attribute.inBandPinMode ? styles.withPin : ""}>
            {attribute.isAsync ? (
                <AsyncAttributeInput
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    onSetSubmitValue={onSetSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                />
            ) : attribute.isNew && !attribute.isValueFromPicker ? (
                //@ts-ignore
                <InputComponent
                    title={attributeLabel}
                    attribute={attribute}
                    fieldName={fieldName}
                    fieldError={fieldError}
                    fieldTouched={fieldTouched}
                    fieldIsInvalid={fieldIsInvalid}
                    fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                    onBlur={onBlur}
                    onSetPickerValue={handleUpdatePickerValue}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                />
            ) : attribute.isPlaceholder ? (
                <PlaceholderComponent
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                />
            ) : attribute.hasPreSelectedSinglePickerValue ? (
                <ReadOnlyAttributeInput
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    onAddNewSubmitValue={onAddNewSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                />
            ) : attribute.hasPreviousPickerValues ? (
                <PickerAttributeInput
                    attribute={attribute}
                    fieldName={fieldName}
                    fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    onAddNewSubmitValue={onAddNewSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                />
            ) : null}
            {attribute.inBandPinMode && (
                <PinInputContainer
                    attribute={attribute}
                    fieldShouldBeAutofocused={pinFieldShouldBeAutofocused}
                    attributeValuesIndex={attributeValuesPinIndex}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                    onBlur={onBlur}
                />
            )}
        </div>
    );
};